import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import type { SchemaElement } from "../../../../api/restaurantSchema";
import type { StageLayout } from "../../../../components/restaurant-schema/SchemaStage";
import { CloseIcon } from "../../../../components/restaurant-schema/schemaIcons";
import type { TableState } from "../../logic/tableStatus";
import { useReservationFormat } from "../../hooks/useReservationFormat";
import TablePopoverBody, { RestorableReservation, type PopoverHandlers } from "./TablePopoverBody";
import { keepVisibleBoxOf, placePopover } from "./popoverPlacement";

/**
 * Друга маса на показаната резервация — поповърът не я покрива; `hasChip` —
 * над нея стои чипът „следваща резервация" и се пази и той.
 */
export interface LinkedTable {
  table: SchemaElement;
  hasChip: boolean;
}

// Ширината на поповъра (`w-[312px]`).
const POPOVER_WIDTH = 312;
// Височината до първото мерене — то е преди рисуване, затова не се вижда.
const ESTIMATED_HEIGHT = 260;

const BADGE_TONE: Record<TableState["kind"], string> = {
  free: "border-schema-fits-edge bg-surface text-schema-fits-ink",
  reserved: "border-schema-reserved-edge-soft bg-schema-reserved text-ink",
  occupied: "border-schema-occupied-edge bg-schema-occupied text-schema-occupied-ink",
};

const PILL_TONE: Record<TableState["kind"], string> = {
  free: "bg-ok-tint text-schema-fits-ink",
  reserved: "bg-brand-tint text-brand-deep-ink",
  occupied: "bg-danger-tint text-schema-occupied-ink",
};

/**
 * Височината на поповъра (по съдържание) — мястото му зависи от нея. Мери се
 * след всеки рендер преди рисуване (setState само при разлика — без цикъл) и
 * при промяна на размера отвътре (късно заредени шрифтове).
 */
const useMeasuredHeight = (ref: RefObject<HTMLElement | null>) => {
  const [height, setHeight] = useState(ESTIMATED_HEIGHT);
  const measure = useCallback(() => {
    const measured = ref.current?.offsetHeight;
    if (measured) setHeight(measured);
  }, [ref]);
  // След всеки рендер — съдържанието се сменя с масата, момента и действията.
  useLayoutEffect(() => measure());
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, measure]);
  return height;
};

/**
 * Поповърът на маса, закотвен до нея върху сцената (рисува се през
 * `renderOverlay` и скролва със схемата). Хедър: номерът в стила на
 * статуса, „Маса 1", „5 места · Основна зала", статус, ×. Застава до масата
 * така, че да не покрива другите маси на показаната резервация
 * (`linkedTables`, `placePopover`). При отваряне фокусът влиза в поповъра
 * (иначе е след всички маси в Tab реда), а при затваряне се връща там,
 * откъдето е дошъл.
 */
const TablePopover = ({
  table,
  linkedTables,
  regionName,
  layout,
  state,
  momentMs,
  nowMs,
  isAtNow,
  handlers,
  onClose,
}: {
  table: SchemaElement;
  // Другите маси на показаната резервация в региона — остават видими.
  linkedTables: LinkedTable[];
  regionName: string;
  layout: StageLayout;
  state: TableState;
  momentMs: number;
  nowMs: number;
  isAtNow: boolean;
  handlers: PopoverHandlers;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  const isRound = table.tableShape === "round";
  const dialogRef = useRef<HTMLDivElement>(null);
  const restorable = handlers.restorableOn(table.id);
  const height = useMeasuredHeight(dialogRef);
  const position = placePopover({
    anchor: layout.boxOf(table),
    keepVisible: linkedTables.map(({ table: linked, hasChip }) =>
      keepVisibleBoxOf(layout.boxOf(linked), layout.scale, hasChip),
    ),
    size: { width: POPOVER_WIDTH, height },
    canvas: { width: layout.width, height: layout.height },
  });

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Без скрол: позицията на платното не бива да скача от фокуса.
    dialogRef.current?.focus({ preventScroll: true });
    return () => {
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, [table.id]);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      tabIndex={-1}
      aria-label={format.tableName(table.name ?? "")}
      // Натискане вътре не бива да стига до платното (то затваря поповъра).
      onPointerDown={(event) => event.stopPropagation()}
      className="dk-elevated absolute z-30 w-[312px] cursor-default overflow-hidden outline-none rounded-[14px] border border-shade-2 bg-surface shadow-[0_2px_6px_rgba(28,25,23,0.08),0_16px_40px_rgba(28,25,23,0.16)]"
      // Позиция, известна чак по време на изпълнение (правило 3, изключение 1).
      style={position}
    >
      <div className="flex items-center gap-2.5 border-b border-shade-2 px-4 py-3">
        <span
          // min-w вместо фиксирана ширина — дълго име („E14", „Бар 1") разширява значката.
          className={`flex h-8 min-w-8 shrink-0 items-center justify-center border-2 px-1 text-[14px] font-bold ${
            isRound ? "rounded-full" : "rounded-[8px]"
          } ${BADGE_TONE[state.kind]}`}
        >
          {table.name}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-bold leading-[1.2] tracking-[-0.2px] text-ink">
            {format.tableName(table.name ?? "")}
          </div>
          {/* Пренася се само между местата и региона — „Основна зала" не се къса на две. */}
          <div className="mt-0.5 text-[12px] font-medium leading-[1.3] text-muted">
            <span className="whitespace-nowrap">{format.seats(table.seats ?? 0)} ·</span>{" "}
            <span className="inline-block max-w-full truncate align-bottom">{regionName}</span>
          </div>
        </div>
        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-[12px] font-bold leading-[1.2] ${PILL_TONE[state.kind]}`}
        >
          {t(`reservations.popover.status.${state.kind}`)}
        </span>
        <button
          type="button"
          onClick={onClose}
          title={t("reservations.popover.close")}
          aria-label={t("reservations.popover.close")}
          className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-muted transition-colors duration-150 hover:bg-shade-1 hover:text-ink"
        >
          <CloseIcon className="size-3.5" />
        </button>
      </div>
      <div className="flex flex-col gap-3 px-4 py-3.5">
        <TablePopoverBody state={state} momentMs={momentMs} nowMs={nowMs} isAtNow={isAtNow} handlers={handlers} />
        {restorable && <RestorableReservation reservation={restorable} handlers={handlers} />}
      </div>
    </div>
  );
};

export default TablePopover;
